import { Link } from 'react-router-dom';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone, CheckCircle, Users, Truck } from 'lucide-react';
import SystemHealthDashboard from '@/components/dashboard/SystemHealthDashboard';
import { api } from '@/services/api';
import type { SipPeersResponse } from '@/types/sip';
import { PageHeader, StatsGrid, StatCard, LoadingState, EmptyState, ErrorState, StatusBadge } from '@/components/common';

export default function Dashboard() {
  const queryClient = useQueryClient();
  const fetching = useIsFetching({ queryKey: ['dashboard'] });
  const peers = useQuery({
    queryKey: ['dashboard', 'sip-peer-preview'],
    queryFn: async () => (await api.get<SipPeersResponse>('/api/sip-peers', { limit: 100, offset: 0 })).data,
    refetchInterval: 30000,
  });
  const rows = peers.data?.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Engine observations and configured peers. Refreshes every 30 seconds."
        onRefresh={() => void queryClient.refetchQueries({ queryKey: ['dashboard'], type: 'active' })}
        isRefreshing={fetching > 0}
      />
      <SystemHealthDashboard />
      <section className="bg-white rounded-lg border p-6 space-y-4">
        <div className="flex flex-wrap justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Configured SIP peers</h2>
            <p className="text-sm text-gray-600">Counts cover a preview of up to 100 peers. Enabled is an administrative setting, not registration or reachability.</p>
          </div>
          <Link className="text-sm font-medium text-blue-700 hover:text-blue-900" to="/connectivity/sip-peers">Manage peers →</Link>
        </div>
        {peers.isLoading ? <LoadingState /> : peers.isError ? (
          <ErrorState title="Failed to load configured peers" message={peers.error.message} onRetry={() => void peers.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState title="No SIP peers configured" description="Configure peers to define your SIP connections." icon={Phone} />
        ) : <>
          <StatsGrid>
            <StatCard label="Peers in preview" value={rows.length} icon={Phone} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
            <StatCard label="Enabled in preview" value={rows.filter(peer => peer.enabled).length} icon={CheckCircle} iconBgColor="bg-gray-100" iconColor="text-gray-600" />
            <StatCard label="Teams in preview" value={rows.filter(peer => peer.type === 'teams').length} icon={Users} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
            <StatCard label="Carriers in preview" value={rows.filter(peer => peer.type === 'carrier').length} icon={Truck} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
          </StatsGrid>
          <div className="overflow-x-auto rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                {['Peer', 'Type', 'Transport', 'Configuration'].map(label => <th key={label} scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">{label}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {rows.slice(0, 5).map(peer => <tr key={peer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm"><div className="font-medium text-gray-900">{peer.name}</div><div className="text-xs text-gray-500">{peer.sipUri}</div></td>
                  <td className="px-6 py-4 text-sm text-gray-600">{peer.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{peer.transport}</td>
                  <td className="px-6 py-4"><StatusBadge status={peer.enabled ? 'enabled' : 'disabled'} /></td>
                </tr>)}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">Showing {Math.min(rows.length, 5)} peers from the preview. Fetched {new Date(peers.dataUpdatedAt).toLocaleString()}.</p>
        </>}
      </section>
    </div>
  );
}
